import React, { useCallback, useEffect, useState } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import { useSelector } from 'react-redux';
import { Box, Button, CircularProgress, Tooltip } from '@mui/material';
import LocalPrintshopIcon from '@mui/icons-material/LocalPrintshop';
import { PrintLabelsModal } from './PrintLabelsModal';
import { GridColDef, GridFilterItem, GridPaginationModel, GridRenderCellParams, GridSortItem } from '@mui/x-data-grid-pro';
import { SharedTable } from '../../components/sharedTable';
import { useAppDispatch } from '../../store';
import {
  selectInbounds,
  selectIsFinishInboundLoading,
  selectIsInboundsLoading,
  selectTotalRowInbounds,
} from '../../store/selectors/inboundsSelector';
import { getInbounds, updateInboundFinishedAt, finishInbounds } from '../../store/slices/inboundsSlice';
import { getStoredItem, removeStoredItem, storeItem } from '../../services/storageService';
import { Inbound, OzonStatus } from '../../models/Inbound';
import { getStateSnapshotFromLocalStorage } from '../../utils/state';
import { useLocation } from 'react-router-dom';
import { RedirectCell } from '../shared';

const defaultSorting: GridSortItem[] = [{ field: 'id', sort: 'desc' }];
const defaultPagination: Partial<GridPaginationModel> = { page: 0, pageSize: 25 };
const defaultFilter: GridFilterItem[] = [
  {
    field: 'ozon_status',
    operator: 'isAnyOf',
    value: ['sent', 'formed', 'arrived'],
  },
];

export const InboundsPage: React.FC = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const entityId = searchParams.get('id');
  const filterById = entityId
    ? [
        {
          id: 'by_id',
          field: 'id',
          operator: '=',
          value: parseInt(entityId, 10),
        },
      ]
    : null;

  const tableName = 'inbounds' + (entityId ? 'OneRow' : '');
  const dispatch = useAppDispatch();

  const inbounds = useSelector(selectInbounds);
  const isFinishInboundLoading = useSelector(selectIsFinishInboundLoading);
  const isInboundsLoading = useSelector(selectIsInboundsLoading);
  const totalRows = useSelector(selectTotalRowInbounds);

  const [isDataNededToBeFetched, setIsDataNededToBeFetched] = useState(true);
  const [isPaginationChange, setIsPaginationChange] = useState(false);
  const [selectedInboundIds, setSelectedInboundIds] = useState<number[]>([]);
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);

  const ozonStatusTitlePair: { [key in OzonStatus]: string } = {
    formed: 'Сформирована',
    arrived: 'Прибыла на склад',
    finished: 'Завершена приемка',
    sent: 'Отправлена',
  };

  let columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'ID',
      type: 'number',
      hideable: false,
    },
    {
      field: 'created_at',
      headerName: 'Создано',
      type: 'dateTime',
    },
    {
      field: 'updated_at',
      headerName: 'Обновлено',
      type: 'dateTime',
    },
    {
      field: 'ozon_status',
      headerName: 'Статус Ozon',
      type: 'singleSelect',
      valueOptions: Object.entries(ozonStatusTitlePair).map((entry) => ({
        label: entry[1],
        value: entry[0],
      })),
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip title={`Обновлено: ${new Date(params.row.ozon_data_fetched_at).toLocaleString('ru-RU')}`}>
          <span>{ozonStatusTitlePair[params.value as OzonStatus]}</span>
        </Tooltip>
      ),
    },
    {
      field: 'postings_count',
      headerName: 'Отправлений',
      type: 'number',
      renderCell: (params) => <RedirectCell url={`/postings-all?entity=ozon_inbound&id=${params.row.id}`} value={params.value} />,
    },
    {
      field: 'scanned_count',
      headerName: 'Сканировано',
      type: 'number',
    },
    {
      field: 'not_scanned_count',
      headerName: 'Не сканировано',
      type: 'number',
    },
    {
      field: 'ozon_start_point_name',
      headerName: 'Отправитель',
      type: 'string',
    },
    {
      field: 'ozon_sent_at',
      headerName: 'Дата отправки',
      type: 'dateTime',
    },
    {
      field: 'ozon_arrived_at',
      headerName: 'Дата прибытия',
      type: 'dateTime',
    },
    {
      field: 'ozon_finished_at',
      headerName: 'Дата завершения',
      type: 'dateTime',
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip title={params.row.finished_at ? `У нас: ${new Date(params.row.finished_at).toLocaleDateString('ru-RU')}` : ''}>
          <span>{params.value ? new Date(params.value).toLocaleDateString('ru-RU') : ''}</span>
        </Tooltip>
      ),
    },
  ];

  if (filterById) {
    columns = columns.map((x) => {
      x.filterable = false;
      x.sortable = false;
      return x;
    });
  }

  const handleSelectionChange = useCallback(
    (pageSelectedRowIds: number[]) => {
      if (isPaginationChange) {
        setIsPaginationChange(false);
        return;
      }

      setSelectedInboundIds(pageSelectedRowIds);
      storeItem('selectedOn' + tableName, pageSelectedRowIds);
    },
    [selectedInboundIds, isPaginationChange]
  );

  const handlePaginationChange = useCallback(() => {
    setIsDataNededToBeFetched(true);
    setIsPaginationChange(true);
  }, []);

  const handleFinishInbound = useCallback(async () => {
    const wrappedIds = selectedInboundIds.map((id) => ({ id }));
    const resultAction = await dispatch(finishInbounds(wrappedIds));

    if (finishInbounds.fulfilled.match(resultAction)) {
      const mapped = resultAction.payload.list.reduce((acc: { [key: number]: string }, inbound: Inbound) => {
        acc[inbound.id] = inbound.finished_at ?? '';
        return acc;
      }, {});
      dispatch(updateInboundFinishedAt(mapped));
      toast.success('Успешно обновлены!');
      setSelectedInboundIds([]);
      removeStoredItem('selectedOn' + tableName);
    }
  }, [dispatch, selectedInboundIds]);

  const dispatchGetInbounds = useCallback(async () => {
    const snapshotState = await getStateSnapshotFromLocalStorage(tableName);
    const resultAction = await dispatch(
      getInbounds({
        filtering: filterById ?? snapshotState?.filter?.filterModel?.items ?? defaultFilter,
        sorting: snapshotState?.sorting?.sortModel ?? defaultSorting,
        ...(snapshotState?.pagination?.paginationModel ?? defaultPagination),
      })
    );

    if (getInbounds.fulfilled.match(resultAction)) {
      setIsDataNededToBeFetched(false);
      const storedSelectedRowIds = getStoredItem<number[]>('selectedOn' + tableName);
      if (storedSelectedRowIds) {
        setSelectedInboundIds(storedSelectedRowIds);
      }
    }
  }, [defaultFilter, defaultSorting, defaultPagination]);

  const handleDataQueryChange = useCallback(() => {
    setIsDataNededToBeFetched(true);
  }, []);

  useEffect(() => {
    if (isDataNededToBeFetched) {
      dispatchGetInbounds();
    }
  }, [isDataNededToBeFetched]);

  return (
    <div>
      <ToastContainer />
      <Box sx={{ display: 'flex', gap: '12px' }}>
        <Button
          variant="contained"
          color="info"
          onClick={handleFinishInbound}
          disabled={isFinishInboundLoading || selectedInboundIds.length < 1}
          startIcon={isFinishInboundLoading ? <CircularProgress size={16} /> : null}
        >
          {isFinishInboundLoading ? 'Обновление...' : 'Завершить приемку'}
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={() => setIsPrintModalOpen(true)}
          disabled={selectedInboundIds.length < 1}
          startIcon={<LocalPrintshopIcon />}
        >
          Распечатать этикетки
        </Button>
      </Box>
      <PrintLabelsModal open={isPrintModalOpen} onClose={() => setIsPrintModalOpen(false)} selectedInboundIds={selectedInboundIds} />
      <SharedTable
        columns={columns}
        disableMultipleRowSelection={true}
        loading={isInboundsLoading}
        isRowSelectable={(params) => params.row.finished_at === null}
        onPaginationChange={handlePaginationChange}
        onSelectionChange={handleSelectionChange}
        onFilterModelChange={handleDataQueryChange}
        onSortModelChange={handleDataQueryChange}
        rowCount={totalRows}
        rows={inbounds}
        selectedRowIds={selectedInboundIds}
        tableName={tableName}
      />
    </div>
  );
};
